import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule, Router } from '@angular/router';
import { AuthService } from './auth.service';
import { Header } from './header';

export type PolicyTab =
  | 'privacy'
  | 'terms'
  | 'cookie'
  | 'seller'
  | 'refund'
  | 'rights'
  | 'dispute'
  | 'ip';

interface PolicyChip {
  id: PolicyTab;
  label: string;
  icon: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-policies',
  host: {
    '(touchstart)': 'onGlobalTouchStart($event)',
    '(touchend)': 'onGlobalTouchEnd($event)',
  },
  imports: [CommonModule, MatIconModule, RouterModule, Header],
  template: `
    <div class="min-h-screen flex flex-col relative overflow-x-hidden bg-transparent text-slate-900 dark:text-zinc-100 transition-colors duration-300">
      
      <!-- Top Navigation Bar -->
      <app-header
        title="Policies"
        subtitle="@policies"
        iconName="policy"
        (back)="goBack()">
      </app-header>

      <!-- Sub-Header Pill Selectors (Matches Profile Page Tabs: border-radius, active states, padding) -->
      <div class="ios-sub-header-liquid">
        <div class="chip-scroll-container" #policiesChips>
          @for (chip of policyChips; track chip.id) {
            <button
              type="button"
              (click)="activeTab.set(chip.id)"
              class="ios-liquid-chip"
              [class.active]="activeTab() === chip.id"
              [id]="'tab-' + chip.id"
            >
              <mat-icon class="text-[18px] transition-transform group-active:scale-110">{{ chip.icon }}</mat-icon>
              <span class="tracking-tight">{{ chip.label }}</span>
            </button>
          }
        </div>
      </div>

      <!-- Main Content Container with Individual Distinct Cards -->
      <main class="flex-1 pt-[116px] md:pt-[128px] pb-16 px-4 md:px-8 max-w-5xl mx-auto w-full animate-ios-fade relative z-10">
        
        <div class="space-y-4 sm:space-y-6">

          <!-- ================= 1. PRIVACY POLICY ================= -->
          @if (activeTab() === 'privacy') {
            <!-- Tab Hero Header Card -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div class="flex items-start sm:items-center gap-3">
                  <div class="w-11 h-11 rounded-2xl bg-ios-blue/10 dark:bg-ios-blue/20 text-ios-blue flex items-center justify-center shrink-0">
                    <mat-icon class="!text-[24px]">privacy_tip</mat-icon>
                  </div>
                  <div>
                    <h1 class="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tight">
                      Privacy Policy & Data Protections
                    </h1>
                    <p class="text-xs text-slate-500 dark:text-zinc-400 font-medium mt-0.5">
                      How Kicgle explicitly collects, verifies, and protects your account details.
                    </p>
                  </div>
                </div>
                <span class="self-start sm:self-center text-[10px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-3 py-1.5 rounded-full border border-emerald-500/20 shrink-0">
                  Strict Data Protection
                </span>
              </div>
            </div>

            <!-- Subsection 1.1 Card: Sign-Up Registration Data -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-ios-blue/10 text-ios-blue flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">how_to_reg</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  1.1 Sign-Up Registration Data
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed mb-4">
                When you register an account with Kicgle, the platform explicitly records essential credentials and identity classifications to provide secure authentication:
              </p>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                <div class="p-3.5 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200/60 dark:border-white/5 flex items-center gap-2">
                  <mat-icon class="text-ios-blue !text-base shrink-0">badge</mat-icon>
                  <span><strong>Full Name & Username:</strong> Public handle and account owner identity</span>
                </div>
                <div class="p-3.5 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200/60 dark:border-white/5 flex items-center gap-2">
                  <mat-icon class="text-ios-blue !text-base shrink-0">mail</mat-icon>
                  <span><strong>Email Address & Password:</strong> Securely hashed credentials via Firebase</span>
                </div>
                <div class="p-3.5 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200/60 dark:border-white/5 flex items-center gap-2">
                  <mat-icon class="text-ios-blue !text-base shrink-0">call</mat-icon>
                  <span><strong>Contact Phone Number:</strong> Two-factor verification and critical recovery</span>
                </div>
                <div class="p-3.5 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200/60 dark:border-white/5 flex items-center gap-2">
                  <mat-icon class="text-purple-500 !text-base shrink-0">category</mat-icon>
                  <span><strong>Entity Classification:</strong> Strictly categorized as <em>"person"</em> or <em>"organization"</em></span>
                </div>
              </div>
            </div>

            <!-- Subsection 1.2 Card: Sign-In & Authentication Logs -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-ios-blue/10 text-ios-blue flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">history</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  1.2 Sign-In & Authentication Logs
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed mb-4">
                To guarantee account integrity and defend against unauthorized access, Kicgle records automated access audit trails:
              </p>
              <ul class="space-y-2 text-xs sm:text-sm text-slate-700 dark:text-zinc-300">
                <li class="flex items-start gap-2">
                  <mat-icon class="text-emerald-500 !text-base shrink-0 mt-0.5">check_circle</mat-icon>
                  <span><strong>Login Timestamps & Active Session State:</strong> Real-time presence indicators and token expiration tracking.</span>
                </li>
                <li class="flex items-start gap-2">
                  <mat-icon class="text-emerald-500 !text-base shrink-0 mt-0.5">check_circle</mat-icon>
                  <span><strong>IP Address & Network Connection Logs:</strong> Used exclusively for anomaly detection and rate-limiting.</span>
                </li>
                <li class="flex items-start gap-2">
                  <mat-icon class="text-emerald-500 !text-base shrink-0 mt-0.5">check_circle</mat-icon>
                  <span><strong>Device User-Agent & Browser Attributes:</strong> Ensuring responsive rendering across mobile PWA and desktop clients.</span>
                </li>
                <li class="flex items-start gap-2">
                  <mat-icon class="text-emerald-500 !text-base shrink-0 mt-0.5">check_circle</mat-icon>
                  <span><strong>Session Renewal & Access Refresh Tokens:</strong> Cryptographically signed tokens stored safely on client devices.</span>
                </li>
              </ul>
            </div>

            <!-- Subsection 2 Card: Automated PAN Document Verification (Tesseract.js) -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-purple-500/10 text-purple-500 flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">badge</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  2. Automated PAN Document Verification (Tesseract.js)
                </h2>
              </div>
              <div class="space-y-3 text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                <p>
                  Kicgle employs client-side optical character recognition (OCR) powered by <strong class="text-purple-600 dark:text-purple-400 font-bold">Tesseract.js</strong> to parse and verify uploaded PAN cards and official government identity documents.
                </p>
                <div class="p-3.5 rounded-xl bg-ios-blue/5 dark:bg-ios-blue/10 border border-ios-blue/20 text-slate-800 dark:text-zinc-200">
                  <strong class="text-ios-blue font-bold flex items-center gap-1.5 mb-1">
                    <mat-icon class="!text-[16px]">info</mat-icon>
                    Entity Label Mapping Standard
                  </strong>
                  Document verification strictly maps verified individual citizen identities to the internal label <code class="px-1.5 py-0.5 rounded bg-ios-blue/15 text-ios-blue font-bold text-xs">"person"</code> rather than "individual", harmonizing all search indexing, roles, and schema collections.
                </div>
                <p>
                  Tesseract.js extracts document numbers, birth dates, and cardholder names in secure worker threads, ensuring rapid verification without human intermediaries.
                </p>
              </div>
            </div>

            <!-- Subsection 3 Card: Purpose-Driven Data Usage -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">center_focus_strong</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  3. Purpose-Driven Data Usage & Confidentiality
                </h2>
              </div>
              <div class="space-y-3 text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                <p>
                  All collected data is utilized <strong class="text-emerald-600 dark:text-emerald-400 font-bold">solely for improving search accuracy, query relevancy, and personalizing the Kicgle search experience</strong> for authenticated users.
                </p>
                <p>
                  Your personal identity and profile documents are kept strictly confidential within Kicgle's native infrastructure and are never traded, brokered, or transferred to third-party ad networks.
                </p>
              </div>
            </div>
          }

          <!-- ================= 2. TERMS OF SERVICE ================= -->
          @if (activeTab() === 'terms') {
            <!-- Tab Hero Header Card -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div class="flex items-start sm:items-center gap-3">
                  <div class="w-11 h-11 rounded-2xl bg-ios-blue/10 dark:bg-ios-blue/20 text-ios-blue flex items-center justify-center shrink-0">
                    <mat-icon class="!text-[24px]">gavel</mat-icon>
                  </div>
                  <div>
                    <h1 class="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tight">
                      Terms of Service & Platform Governance
                    </h1>
                    <p class="text-xs text-slate-500 dark:text-zinc-400 font-medium mt-0.5">
                      Rules governing search conduct, entity classifications, and user obligations.
                    </p>
                  </div>
                </div>
                <span class="self-start sm:self-center text-[10px] font-black uppercase tracking-wider text-ios-blue bg-ios-blue/10 px-3 py-1.5 rounded-full border border-ios-blue/20 shrink-0">
                  User Agreement
                </span>
              </div>
            </div>

            <!-- Subsection 1 Card: User Account & Classification Rules -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-ios-blue/10 text-ios-blue flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">account_box</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  1. User Account & Entity Classifications
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed mb-4">
                When registering on Kicgle, users must supply accurate information. Accounts are partitioned into two recognized entity classifications:
              </p>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div class="p-4 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200/60 dark:border-white/5">
                  <div class="flex items-center gap-2 text-ios-blue font-bold text-xs mb-1">
                    <mat-icon class="!text-[18px]">person</mat-icon>
                    <span>"person" Entity</span>
                  </div>
                  <p class="text-xs text-slate-600 dark:text-zinc-300">
                    Individual citizens, researchers, and creators enjoying personal search, saved bookmarks, and interactive modules.
                  </p>
                </div>
                <div class="p-4 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200/60 dark:border-white/5">
                  <div class="flex items-center gap-2 text-purple-500 font-bold text-xs mb-1">
                    <mat-icon class="!text-[18px]">corporate_fare</mat-icon>
                    <span>"organization" Entity</span>
                  </div>
                  <p class="text-xs text-slate-600 dark:text-zinc-300">
                    Registered businesses, enterprises, institutions, and verified publishers managing marketplace and product listings.
                  </p>
                </div>
              </div>
            </div>

            <!-- Subsection 2 Card: Automated Verification Compliance -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">document_scanner</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  2. Automated Document Verification & Anti-Fraud
                </h2>
              </div>
              <div class="space-y-3 text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                <p>
                  Official identity credentials undergo real-time automated validation via the <strong class="font-bold text-slate-900 dark:text-white">Tesseract.js</strong> optical engine to verify legitimacy and prevent impersonation.
                </p>
                <div class="p-3.5 rounded-xl bg-amber-500/5 border border-amber-500/20 text-amber-800 dark:text-amber-300 text-xs">
                  <strong>Zero-Tolerance Policy:</strong> Submitting altered, fabricated, or forged documentation results in permanent account suspension and revocation of all associated content.
                </div>
              </div>
            </div>

            <!-- Subsection 3 Card: Search Platform Conduct -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-ios-blue/10 text-ios-blue flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">search</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  3. Search Usage & Platform Integrity
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                Kicgle services are provided solely for lawful discovery, product research, media exploration, and authorized profile networking. Users agree not to execute automated scraping scripts, denial-of-service attempts, unauthorized reverse-engineering, or security circumvention.
              </p>
            </div>
          }

          <!-- ================= 3. COOKIE POLICY ================= -->
          @if (activeTab() === 'cookie') {
            <!-- Tab Hero Header Card -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div class="flex items-start sm:items-center gap-3">
                  <div class="w-11 h-11 rounded-2xl bg-purple-500/10 dark:bg-purple-500/20 text-purple-500 flex items-center justify-center shrink-0">
                    <mat-icon class="!text-[24px]">cookie</mat-icon>
                  </div>
                  <div>
                    <h1 class="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tight">
                      Cookie Policy & Session Storage
                    </h1>
                    <p class="text-xs text-slate-500 dark:text-zinc-400 font-medium mt-0.5">
                      Essential session tokens and local preferences used to personalize search.
                    </p>
                  </div>
                </div>
                <span class="self-start sm:self-center text-[10px] font-black uppercase tracking-wider text-purple-600 dark:text-purple-400 bg-purple-500/10 px-3 py-1.5 rounded-full border border-purple-500/20 shrink-0">
                  Storage Rules
                </span>
              </div>
            </div>

            <!-- Subsection 1 Card: Essential Sign-In Session Cookies -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-ios-blue/10 text-ios-blue flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">token</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  1. Essential Sign-In & Session Authentication Tokens
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                Kicgle uses secure, encrypted browser session cookies and local storage to maintain authenticated sign-in status across search sessions, protect session tokens against cross-site scripting, and safeguard account identity.
              </p>
            </div>

            <!-- Subsection 2 Card: Search Personalization & Preferences -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">tune</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  2. Search Personalization & UI Preferences
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                Local preference storage preserves your interface customizations—including dark mode preference, active search filter categories, and fluid animations—<strong class="text-emerald-600 dark:text-emerald-400 font-bold">solely to maximize search responsiveness and provide a tailored user experience</strong>.
              </p>
            </div>

            <!-- Subsection 3 Card: Preference Management -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-ios-blue/10 text-ios-blue flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">settings</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  3. Preference Management & Clearing Cookies
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                You can manage or clear your storage data at any time through your browser's security settings. Please note that clearing essential session cookies will sign you out and require re-authentication.
              </p>
            </div>
          }

          <!-- ================= 4. SELLER POLICY ================= -->
          @if (activeTab() === 'seller') {
            <!-- Tab Hero Header Card -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div class="flex items-start sm:items-center gap-3">
                  <div class="w-11 h-11 rounded-2xl bg-amber-500/10 dark:bg-amber-500/20 text-amber-500 flex items-center justify-center shrink-0">
                    <mat-icon class="!text-[24px]">storefront</mat-icon>
                  </div>
                  <div>
                    <h1 class="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tight">
                      Seller Policy & Marketplace Modules
                    </h1>
                    <p class="text-xs text-slate-500 dark:text-zinc-400 font-medium mt-0.5">
                      Requirements for listing goods, services, and digital assets on Kicgle product modules.
                    </p>
                  </div>
                </div>
                <span class="self-start sm:self-center text-[10px] font-black uppercase tracking-wider text-amber-600 dark:text-amber-400 bg-amber-500/10 px-3 py-1.5 rounded-full border border-amber-500/20 shrink-0">
                  Merchant Standards
                </span>
              </div>
            </div>

            <!-- Subsection 1 Card: Merchant Qualification & Verification -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-ios-blue/10 text-ios-blue flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">verified</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  1. Merchant Onboarding & Verified Identity
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                Sellers publishing products, digital assets, or service offerings must complete PAN document verification. Both <strong class="text-ios-blue font-bold">"person"</strong> and <strong class="text-purple-500 font-bold">"organization"</strong> accounts must be authenticated prior to publishing listings into public search indexes.
              </p>
            </div>

            <!-- Subsection 2 Card: Catalog Accuracy & Listing Compliance -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">inventory_2</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  2. Catalog Accuracy & Item Representation
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                Item titles, descriptions, pricing, inventory stock, and media previews must accurately reflect the goods or digital downloads offered. Hidden fees, deceptive specifications, or bait-and-switch pricing are strictly forbidden.
              </p>
            </div>

            <!-- Subsection 3 Card: Order Fulfillment & Prohibited Goods -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-rose-500/10 text-rose-500 flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">block</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  3. Order Fulfillment & Prohibited Goods
                </h2>
              </div>
              <div class="space-y-3 text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                <p>
                  Merchants must dispatch confirmed orders within stated delivery windows and supply authentic tracking info.
                </p>
                <div class="p-3.5 rounded-xl bg-rose-500/5 border border-rose-500/20 text-rose-800 dark:text-rose-300 text-xs">
                  <strong>Strictly Prohibited:</strong> Counterfeit goods, unauthorized media licenses, hazardous substances, and infringing materials. Infractions result in instant store termination.
                </div>
              </div>
            </div>
          }

          <!-- ================= 5. REFUND POLICY ================= -->
          @if (activeTab() === 'refund') {
            <!-- Tab Hero Header Card -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div class="flex items-start sm:items-center gap-3">
                  <div class="w-11 h-11 rounded-2xl bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-500 flex items-center justify-center shrink-0">
                    <mat-icon class="!text-[24px]">currency_exchange</mat-icon>
                  </div>
                  <div>
                    <h1 class="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tight">
                      Refund & Purchase Protection Policy
                    </h1>
                    <p class="text-xs text-slate-500 dark:text-zinc-400 font-medium mt-0.5">
                      Return conditions, refund processing, and buyer protection guidelines.
                    </p>
                  </div>
                </div>
                <span class="self-start sm:self-center text-[10px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-3 py-1.5 rounded-full border border-emerald-500/20 shrink-0">
                  Buyer Protection
                </span>
              </div>
            </div>

            <!-- Subsection 1 Card: Refund Eligibility -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">assignment_return</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  1. Refund Eligibility Criteria
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed mb-3">
                Purchases made through Kicgle product modules are eligible for reimbursement under the following circumstances:
              </p>
              <ul class="space-y-2 text-xs sm:text-sm text-slate-700 dark:text-zinc-300">
                <li class="flex items-start gap-2">
                  <mat-icon class="text-emerald-500 !text-base shrink-0 mt-0.5">check_circle</mat-icon>
                  <span>The item received is damaged, defective, or materially distinct from its catalog listing.</span>
                </li>
                <li class="flex items-start gap-2">
                  <mat-icon class="text-emerald-500 !text-base shrink-0 mt-0.5">check_circle</mat-icon>
                  <span>The merchant fails to dispatch or fulfill the purchase within agreed fulfillment periods.</span>
                </li>
                <li class="flex items-start gap-2">
                  <mat-icon class="text-emerald-500 !text-base shrink-0 mt-0.5">check_circle</mat-icon>
                  <span>Digital downloads or media fail to open or operate as specified in the verified description.</span>
                </li>
              </ul>
            </div>

            <!-- Subsection 2 Card: Return Request Window -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-ios-blue/10 text-ios-blue flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">schedule</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  2. Return Request Windows
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                Buyers must file a return or refund ticket within <strong class="text-ios-blue font-bold">14 calendar days</strong> of receiving physical goods, or within <strong>48 hours</strong> following digital purchase unlock.
              </p>
            </div>

            <!-- Subsection 3 Card: Reimbursement Processing -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-purple-500/10 text-purple-500 flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">account_balance_wallet</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  3. Reimbursement Processing & Timelines
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                Approved reimbursements are refunded directly to the buyer's original payment method within 5–7 business days following item return inspection or dispute resolution.
              </p>
            </div>
          }

          <!-- ================= 6. USER RIGHTS & PORTABILITY ================= -->
          @if (activeTab() === 'rights') {
            <!-- Tab Hero Header Card -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div class="flex items-start sm:items-center gap-3">
                  <div class="w-11 h-11 rounded-2xl bg-purple-500/10 dark:bg-purple-500/20 text-purple-500 flex items-center justify-center shrink-0">
                    <mat-icon class="!text-[24px]">account_box</mat-icon>
                  </div>
                  <div>
                    <h1 class="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tight">
                      User Rights & Data Portability
                    </h1>
                    <p class="text-xs text-slate-500 dark:text-zinc-400 font-medium mt-0.5">
                      Your rights to access, export, correct, or request deletion of your personal data.
                    </p>
                  </div>
                </div>
                <span class="self-start sm:self-center text-[10px] font-black uppercase tracking-wider text-purple-600 dark:text-purple-400 bg-purple-500/10 px-3 py-1.5 rounded-full border border-purple-500/20 shrink-0">
                  Data Sovereignty
                </span>
              </div>
            </div>

            <!-- Subsection 1 Card: Right to Access & Data Portability -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-ios-blue/10 text-ios-blue flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">download</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  1. Right to Access & Data Portability (JSON Export)
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                Kicgle upholds your complete data sovereignty. Users have the unencumbered right to request an automated, machine-readable JSON export of all personal profile records, authentication logs, and search preferences at any time.
              </p>
            </div>

            <!-- Subsection 2 Card: Right to Rectification -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">edit</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  2. Right to Rectification & Profile Correction
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                You may modify, update, or correct your personal information, phone number, display avatar, and entity classification preferences at any time inside the Kicgle Settings portal.
              </p>
            </div>

            <!-- Subsection 3 Card: Right to Erasure -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-rose-500/10 text-rose-500 flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">delete_forever</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  3. Right to Erasure (Complete Account Deletion)
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                Authenticated account owners may initiate permanent account deletion. Upon verification, all associated personal documents, credentials, authentication sessions, and stored preferences are permanently purged from Kicgle production databases.
              </p>
            </div>
          }

          <!-- ================= 7. DISPUTE RESOLUTION ================= -->
          @if (activeTab() === 'dispute') {
            <!-- Tab Hero Header Card -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div class="flex items-start sm:items-center gap-3">
                  <div class="w-11 h-11 rounded-2xl bg-amber-500/10 dark:bg-amber-500/20 text-amber-500 flex items-center justify-center shrink-0">
                    <mat-icon class="!text-[24px]">balance</mat-icon>
                  </div>
                  <div>
                    <h1 class="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tight">
                      Dispute Resolution & Platform Governance
                    </h1>
                    <p class="text-xs text-slate-500 dark:text-zinc-400 font-medium mt-0.5">
                      Procedures for resolving merchant-buyer controversies and platform grievances.
                    </p>
                  </div>
                </div>
                <span class="self-start sm:self-center text-[10px] font-black uppercase tracking-wider text-amber-600 dark:text-amber-400 bg-amber-500/10 px-3 py-1.5 rounded-full border border-amber-500/20 shrink-0">
                  Mediation Protocol
                </span>
              </div>
            </div>

            <!-- Subsection 1 Card: Informal Negotiation -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-ios-blue/10 text-ios-blue flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">forum</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  1. Informal Negotiation & Internal Resolution
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                In the event of controversies concerning search placements, merchant transactions, or entity categorizations, parties agree to first pursue amicable informal resolution via Kicgle's dedicated support and compliance channels.
              </p>
            </div>

            <!-- Subsection 2 Card: Neutral Platform Mediation -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-purple-500/10 text-purple-500 flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">gavel</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  2. Neutral Platform Mediation
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                If informal negotiation does not conclude within 15 calendar days, Kicgle administrators provide neutral mediation based upon verifiable transaction ledgers, Tesseract.js validation records, and documented communication history.
              </p>
            </div>

            <!-- Subsection 3 Card: Binding Determination -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">policy</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  3. Binding Determination & Policy Enforcement
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                Mediation rulings rendered by Kicgle platform governance are definitive and binding across all platform transactions, merchant standings, and verified community profiles.
              </p>
            </div>
          }

          <!-- ================= 8. INTELLECTUAL PROPERTY ================= -->
          @if (activeTab() === 'ip') {
            <!-- Tab Hero Header Card -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div class="flex items-start sm:items-center gap-3">
                  <div class="w-11 h-11 rounded-2xl bg-ios-blue/10 dark:bg-ios-blue/20 text-ios-blue flex items-center justify-center shrink-0">
                    <mat-icon class="!text-[24px]">copyright</mat-icon>
                  </div>
                  <div>
                    <h1 class="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tight">
                      Intellectual Property & Copyright
                    </h1>
                    <p class="text-xs text-slate-500 dark:text-zinc-400 font-medium mt-0.5">
                      Protection of Kicgle brand assets, user content ownership, and DMCA policy.
                    </p>
                  </div>
                </div>
                <span class="self-start sm:self-center text-[10px] font-black uppercase tracking-wider text-ios-blue bg-ios-blue/10 px-3 py-1.5 rounded-full border border-ios-blue/20 shrink-0">
                  IP Protection
                </span>
              </div>
            </div>

            <!-- Subsection 1 Card: Kicgle Proprietary Assets -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-ios-blue/10 text-ios-blue flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">verified</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  1. Kicgle Brand Assets & Proprietary Code
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                The Kicgle brand name, logo marks, <span class="font-mono text-ios-blue font-bold">&#64;policies</span> identifiers, search algorithms, and liquid glass user interface styling tokens are protected proprietary assets of Kicgle.
              </p>
            </div>

            <!-- Subsection 2 Card: User Content Ownership -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-purple-500/10 text-purple-500 flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">cloud_upload</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  2. User-Generated Content Ownership & License
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                Users retain full intellectual ownership of photos, creative assets, and product catalog descriptions uploaded to Kicgle. By uploading, users grant Kicgle a limited non-exclusive license strictly to index, render, and display media in search results.
              </p>
            </div>

            <!-- Subsection 3 Card: DMCA Notice & Copyright Takedowns -->
            <div class="bg-white dark:bg-zinc-900 rounded-2xl sm:rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-5 sm:p-7 relative overflow-hidden">
              <div class="flex items-center gap-2.5 mb-3">
                <div class="w-8 h-8 rounded-xl bg-rose-500/10 text-rose-500 flex items-center justify-center shrink-0">
                  <mat-icon class="!text-[18px]">report_problem</mat-icon>
                </div>
                <h2 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  3. Copyright Infringement & DMCA Notices
                </h2>
              </div>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                If you believe your copyrighted work is displayed without authorization on Kicgle search or catalog listings, notify our compliance office at <strong class="text-ios-blue font-bold">compliance&#64;kicgle.com</strong> for prompt investigation and takedown.
              </p>
            </div>
          }

        </div>

      </main>
    </div>
  `,
})
export class PoliciesView {
  authService = inject(AuthService);
  location = inject(Location);
  router = inject(Router);
  activeTab = signal<PolicyTab>('privacy');

  policyChips: PolicyChip[] = [
    { id: 'privacy', label: 'Privacy Policy', icon: 'security' },
    { id: 'terms', label: 'Terms of Service', icon: 'gavel' },
    { id: 'cookie', label: 'Cookie Policy', icon: 'cookie' },
    { id: 'seller', label: 'Seller Policy', icon: 'storefront' },
    { id: 'refund', label: 'Refund Policy', icon: 'currency_exchange' },
    { id: 'rights', label: 'User Rights & Portability', icon: 'account_box' },
    { id: 'dispute', label: 'Dispute Resolution', icon: 'balance' },
    { id: 'ip', label: 'Intellectual Property', icon: 'copyright' },
  ];

  // Touch gesture state for horizontal swiping between sub-header chips
  private touchStartX = 0;
  private touchStartY = 0;
  private touchEndX = 0;
  private touchEndY = 0;

  onGlobalTouchStart(event: TouchEvent) {
    if (event.touches.length > 1) return;
    this.touchStartX = event.changedTouches[0].screenX;
    this.touchStartY = event.changedTouches[0].screenY;
  }

  onGlobalTouchEnd(event: TouchEvent) {
    if (event.changedTouches.length === 0) return;
    this.touchEndX = event.changedTouches[0].screenX;
    this.touchEndY = event.changedTouches[0].screenY;
    this.handleHorizontalSwipe();
  }

  private handleHorizontalSwipe() {
    const diffX = this.touchStartX - this.touchEndX;
    const diffY = this.touchStartY - this.touchEndY;
    const swipeThreshold = 50;

    if (Math.abs(diffX) > swipeThreshold && Math.abs(diffX) > Math.abs(diffY) * 1.5) {
      const currentIndex = this.policyChips.findIndex(c => c.id === this.activeTab());
      if (diffX > 0) {
        // Swipe left -> Next chip tab
        if (currentIndex < this.policyChips.length - 1) {
          const nextTab = this.policyChips[currentIndex + 1].id;
          this.activeTab.set(nextTab);
          this.scrollToChip(nextTab);
        }
      } else {
        // Swipe right -> Previous chip tab
        if (currentIndex > 0) {
          const prevTab = this.policyChips[currentIndex - 1].id;
          this.activeTab.set(prevTab);
          this.scrollToChip(prevTab);
        }
      }
    }
  }

  private scrollToChip(tabId: string) {
    if (typeof document !== 'undefined') {
      setTimeout(() => {
        const el = document.getElementById('tab-' + tabId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }, 50);
    }
  }

  goBack() {
    const hasPreviousPage =
      typeof window !== 'undefined' &&
      window.history.length > 1 &&
      (window.history.state?.navigationId === undefined || window.history.state.navigationId > 1);
    if (hasPreviousPage) {
      this.location.back();
    } else {
      this.router.navigate(['/search']);
    }
  }
}
